import React from 'react';
import { motion, HTMLMotionProps, MotionProps } from 'framer-motion';

type MotionElementType = 'div' | 'h1' | 'h2' | 'p' | 'img';

interface OptimizedMotionProps extends Omit<HTMLMotionProps<'div'>, 'as'> {
    children?: React.ReactNode;
    className?: string;
    style?: React.CSSProperties;
    type?: 'fade' | 'slide-up' | 'slide-down' | 'slide-left' | 'slide-right';
    delay?: number;
    duration?: number;
    once?: boolean;
    src?: string;
    alt?: string;
}

interface OptimizedMotionElementProps extends OptimizedMotionProps {
    as: MotionElementType;
}

const variants = {
    fade: {
        initial: { opacity: 0 },
        animate: { opacity: 1 },
        exit: { opacity: 0 },
    },
    'slide-up': {
        initial: { opacity: 0, y: 20 },
        animate: { opacity: 1, y: 0 },
        exit: { opacity: 0, y: -20 },
    },
    'slide-down': {
        initial: { opacity: 0, y: -20 },
        animate: { opacity: 1, y: 0 },
        exit: { opacity: 0, y: 20 },
    },
    'slide-left': {
        initial: { opacity: 0, x: 20 },
        animate: { opacity: 1, x: 0 },
        exit: { opacity: 0, x: -20 },
    },
    'slide-right': {
        initial: { opacity: 0, x: -20 },
        animate: { opacity: 1, x: 0 },
        exit: { opacity: 0, x: 20 },
    },
};

const motionComponents: Record<MotionElementType, any> = {
    div: motion.div,
    h1: motion.h1,
    h2: motion.h2,
    p: motion.p,
    img: motion.img,
} as const;

const OptimizedMotionBase: React.FC<OptimizedMotionElementProps> = ({
    children,
    className,
    style,
    type = 'fade',
    delay = 0,
    duration = 0.5,
    once = true,
    as,
    ...props
}) => {
    const variant = variants[type];
    const MotionComponent = motionComponents[as];

    return (
        <MotionComponent
            className={className}
            style={{
                ...style,
                willChange: 'transform, opacity',
                transform: 'translateZ(0)',
                backfaceVisibility: 'hidden',
                WebkitFontSmoothing: 'antialiased',
                WebkitTransform: 'translateZ(0)',
                WebkitBackfaceVisibility: 'hidden',
                WebkitPerspective: '1000px',
            }}
            initial="initial"
            whileInView="animate"
            exit="exit"
            viewport={{ once, amount: 0.2 }}
            variants={variant}
            transition={{
                duration,
                delay,
                ease: [0.25, 0.1, 0.25, 1],
                type: 'tween',
            }}
            {...props}
        >
            {children}
        </MotionComponent>
    );
};

export const OptimizedMotion: React.FC<OptimizedMotionProps> = (props) => (
    <OptimizedMotionBase as="div" {...props} />
);

export const OptimizedMotionH1: React.FC<OptimizedMotionProps> = (props) => (
    <OptimizedMotionBase as="h1" {...props} />
);

export const OptimizedMotionH2: React.FC<OptimizedMotionProps> = (props) => (
    <OptimizedMotionBase as="h2" {...props} />
);

export const OptimizedMotionP: React.FC<OptimizedMotionProps> = (props) => (
    <OptimizedMotionBase as="p" {...props} />
);
export const OptimizedMotionImg: React.FC<OptimizedMotionProps> = (props) => (
    <OptimizedMotionBase as="img" {...props} />
);
